import { Label } from '@ui/Label';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import Fuse from 'fuse.js';
import { makeStyles } from 'tss-react/mui';
import { useUser } from '@client/hooks/use-user.hooks';
import { isAdmin } from '@shared/helpers/user.helpers';
import {
  AiScopeDescriptor,
  AiToolDescriptor,
} from '@shared/models/ai-chat.model';
import { humanizeJsonSchema } from './humanize-schema.helpers';

type SchemaView = 'readable' | 'json';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(2),
    overflow: 'auto',
    height: '100%',
  },
  searchRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
  },
  scopeSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  scopeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(3),
  },
  instructionsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxWidth: 900,
  },
  instructions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    margin: 0,
    padding: theme.spacing(3),
    background: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
  },
  toolsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  toolsList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: theme.spacing(3),
  },
  toolCard: {
    background: theme.palette.background.default,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  toolContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    padding: theme.spacing(3),
    flex: 1,
    '&:last-child': {
      paddingBottom: theme.spacing(3),
    },
  },
  toolSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  toolHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
  },
  toolName: {
    fontFamily: 'monospace',
    fontWeight: 600,
    color: theme.palette.primary.main,
    wordBreak: 'break-word',
  },
  toolDescription: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  schema: {
    fontFamily: 'monospace',
    fontSize: 12,
    margin: 0,
    padding: theme.spacing(3),
    background: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
    maxHeight: 300,
  },
  schemaModal: {
    fontFamily: 'monospace',
    fontSize: 12,
    margin: 0,
    padding: theme.spacing(3),
    background: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
  },
  highlight: {
    backgroundColor: '#e3f2fd',
    color: '#0d47a1',
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  tabLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 600,
  },
}));

const HighlightText: React.FC<{
  text: string;
  query: string;
  highlightClass: string;
}> = ({ text, query, highlightClass }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={i} className={highlightClass}>
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
};

const ParagraphText: React.FC<{
  text: string;
  query?: string;
  highlightClass?: string;
  className?: string;
  textProps?: React.ComponentProps<typeof Typography>;
}> = ({ text, query, highlightClass, className, textProps }) => {
  const lines = text.split('\n').filter((line) => line.trim().length > 0);
  return (
    <Box className={className}>
      {lines.map((line, i) => (
        <Typography key={i} {...textProps}>
          {query && highlightClass ? (
            <HighlightText
              text={line}
              query={query}
              highlightClass={highlightClass}
            />
          ) : (
            line
          )}
        </Typography>
      ))}
    </Box>
  );
};

export const AdminAIChatTools: React.FC = () => {
  const { classes } = useStyles();
  const user = useUser();
  const disabled = useMemo(() => !user || !isAdmin(user), [user]);
  const [scopes, setScopes] = useState<AiScopeDescriptor[] | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [schemaView, setSchemaView] = useState<SchemaView>('readable');
  const [selectedTool, setSelectedTool] = useState<AiToolDescriptor | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState<string>('all');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get<{ scopes: AiScopeDescriptor[] }>(
        '/ai-chat-tools',
      );
      setScopes(data.scopes);
      setError(undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load AI chat tools');
    }
  }, []);

  useEffect(() => {
    if (!disabled) load().catch(console.error);
  }, [disabled, load]);

  const filteredScopes = useMemo(() => {
    if (!scopes) return [];
    if (!search) {
      return scopes.map((s) => ({ ...s, filteredTools: s.tools }));
    }

    return scopes.map((scope) => {
      const fuse = new Fuse(scope.tools, {
        keys: ['name', 'description'],
        threshold: 0.3,
        ignoreLocation: true,
      });
      const results = fuse.search(search);
      return {
        ...scope,
        filteredTools: results.map((r) => r.item),
      };
    });
  }, [scopes, search]);

  const tabData = useMemo(() => {
    if (!scopes) return [];
    const allTotal = scopes.reduce((acc, s) => acc + s.tools.length, 0);
    const allMatch = filteredScopes.reduce(
      (acc, s) => acc + s.filteredTools.length,
      0,
    );

    return [
      { id: 'all', label: 'All', total: allTotal, match: allMatch },
      ...scopes.map((s) => {
        const filtered = filteredScopes.find((fs) => fs.id === s.id);
        return {
          id: s.id,
          label: s.id.charAt(0).toUpperCase() + s.id.slice(1),
          total: s.tools.length,
          match: filtered?.filteredTools.length ?? 0,
        };
      }),
    ];
  }, [scopes, filteredScopes]);

  if (disabled) {
    return (
      <Box
        sx={{
          p: 4,
        }}
      >
        <Typography variant="h6">Not allowed</Typography>
      </Box>
    );
  }

  const renderSchemaText = (val: unknown) => {
    const text =
      schemaView === 'readable'
        ? humanizeJsonSchema(val)
        : JSON.stringify(val, null, 2);
    if (schemaView !== 'readable') return text;
    return text.split('\n').map((line, i) => {
      const parts = line.split(/(\*\*[^*]+\*\*)/);
      return (
        <React.Fragment key={i}>
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j}>{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
          {i < text.split('\n').length - 1 && '\n'}
        </React.Fragment>
      );
    });
  };

  return (
    <Box className={classes.root}>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        Registered scopes and the tools each one exposes to the AI chat
        pipeline. Parameters are the strict JSON Schema sent to OpenAI.
      </Typography>

      <Box className={classes.searchRow}>
        <TextField
          size="small"
          placeholder="Filter tools by name or description"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={schemaView}
          onChange={(_, v: SchemaView | null) => v && setSchemaView(v)}
        >
          <ToggleButton value="readable">Readable</ToggleButton>
          <ToggleButton value="json">JSON</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {tabData.length > 0 && (
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          className={classes.tabs}
          variant="scrollable"
          scrollButtons={false}
        >
          {tabData.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              label={
                <Box className={classes.tabLabel}>
                  {tab.label}
                  <Label
                    label={search ? `${tab.match}/${tab.total}` : tab.total}
                    color={activeTab === tab.id ? 'primary' : 'default'}
                    sx={{ ml: 1 }}
                  />
                </Box>
              }
            />
          ))}
        </Tabs>
      )}

      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}

      {!scopes && !error && (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          Loading…
        </Typography>
      )}

      {filteredScopes
        .filter((scope) => activeTab === 'all' || activeTab === scope.id)
        .map((scope) => {
          if (scope.filteredTools.length === 0) return null;

          return (
            <Paper
              key={scope.id}
              elevation={2}
              className={classes.scopeSection}
            >
              <Box className={classes.scopeHeader}>
                <Typography variant="h5">
                  Scope: {scope.id.charAt(0).toUpperCase() + scope.id.slice(1)}
                </Typography>
                <Label label={`${scope.tools.length} tools`} />
                {search && (
                  <Label label={`${scope.filteredTools.length} match`} />
                )}
              </Box>

              <Box className={classes.instructionsSection}>
                <Typography variant="h6" gutterBottom>
                  System instructions
                </Typography>
                <ParagraphText
                  text={scope.instructions}
                  className={classes.instructions}
                  textProps={{
                    variant: 'body2',
                    sx: { fontFamily: 'monospace', fontSize: 12 },
                  }}
                />
              </Box>

              <Divider />

              <Box className={classes.toolsSection}>
                <Typography variant="h6">Tools</Typography>

                {Array.from(
                  scope.filteredTools.reduce((acc, t) => {
                    const domain = t.domain;
                    if (!acc.has(domain)) acc.set(domain, []);
                    acc.get(domain)!.push(t);
                    return acc;
                  }, new Map<string, AiToolDescriptor[]>()),
                ).map(([domain, domainTools]) => (
                  <Box key={domain} sx={{ mb: 4 }}>
                    <Typography
                      variant="overline"
                      sx={{
                        display: 'block',
                        mb: 2,
                        fontWeight: 700,
                        letterSpacing: 1.2,
                        color:
                          domain === scope.id
                            ? 'primary.main'
                            : 'text.secondary',
                      }}
                    >
                      {domain.toUpperCase()} {domain !== scope.id && '(shared)'}
                    </Typography>
                    <Box className={classes.toolsList}>
                      {domainTools.map((tool) => (
                        <Card
                          key={tool.name}
                          variant="outlined"
                          className={classes.toolCard}
                        >
                          <CardContent className={classes.toolContent}>
                            <Box className={classes.toolHeader}>
                              <Typography
                                variant="h6"
                                className={classes.toolName}
                              >
                                <HighlightText
                                  text={tool.name}
                                  query={search}
                                  highlightClass={classes.highlight}
                                />
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => setSelectedTool(tool)}
                                title="Open in modal"
                              >
                                <OpenInFullIcon fontSize="small" />
                              </IconButton>
                            </Box>

                            <Box className={classes.toolSection}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color: 'text.secondary',
                                }}
                              >
                                Description
                              </Typography>
                              <ParagraphText
                                text={tool.description}
                                query={search}
                                highlightClass={classes.highlight}
                                className={classes.toolDescription}
                                textProps={{ variant: 'body2' }}
                              />
                            </Box>

                            <Box className={classes.toolSection}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color: 'text.secondary',
                                }}
                              >
                                Parameters
                                {schemaView === 'json' &&
                                  ' (strict JSON Schema)'}
                              </Typography>
                              <pre className={classes.schema}>
                                {renderSchemaText(tool.parameters)}
                              </pre>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          );
        })}

      <Dialog
        open={!!selectedTool}
        onClose={() => setSelectedTool(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedTool && (
          <>
            <DialogTitle
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 2,
              }}
            >
              <Typography variant="h6" className={classes.toolName}>
                {selectedTool.name}
              </Typography>
              <IconButton size="small" onClick={() => setSelectedTool(null)}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers className={classes.toolContent}>
              <Box className={classes.toolSection}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Description
                </Typography>
                <ParagraphText
                  text={selectedTool.description}
                  className={classes.toolDescription}
                  textProps={{ variant: 'body2' }}
                />
              </Box>

              <Box className={classes.toolSection}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  Parameters
                  {schemaView === 'json' && ' (strict JSON Schema)'}
                </Typography>
                <pre className={classes.schemaModal}>
                  {renderSchemaText(selectedTool.parameters)}
                </pre>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedTool(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};
